import { Routes } from '@/constants/Routes';
import type { PageHref } from '@/constants/Routes';
import { Env } from '@/utils/env';

type EnvValues = { dev: string; prod: string };

export type EmbeddedAppDefinition = {
  id: string;
  label: string;
  route: PageHref;
  origin: EnvValues;
  audience: EnvValues;
};

export type ResolvedEmbeddedApp = {
  id: EmbeddedAppId;
  label: string;
  route: PageHref;
  origin: string;
  audience: string;
};

const REGISTRY = {
  'supply-chain': {
    id: 'supply-chain',
    label: 'Supply Chain App',
    route: Routes.EMBEDDED_SUPPLY_CHAIN,
    origin: {
      dev: 'https://supply-chain-dev.mantisanalytics.com',
      prod: 'https://supply-chain.mantisanalytics.com',
    },
    audience: {
      dev: 'https://supply-chain-dev.mantisanalytics.com/api',
      prod: 'https://supply-chain.mantisanalytics.com/api',
    },
  },
  'intelligence-dashboard': {
    id: 'intelligence-dashboard',
    label: 'Intelligence Dashboard App',
    route: Routes.EMBEDDED_INTELLIGENCE_DASHBOARD,
    origin: {
      dev: 'https://intelligence-dashboard-dev.mantisanalytics.com',
      prod: 'https://intelligence-dashboard.mantisanalytics.com',
    },
    audience: {
      dev: 'https://intelligence-dashboard-dev.mantisanalytics.com/api',
      prod: 'https://intelligence-dashboard.mantisanalytics.com/api',
    },
  },
} as const satisfies Record<string, EmbeddedAppDefinition>;

export type EmbeddedAppId = keyof typeof REGISTRY;

// Local-dev origin overrides (e.g. http://localhost:5173). Must be literal `process.env.X` accesses so Next inlines them.
const getOriginOverrides = (): Partial<Record<EmbeddedAppId, string | undefined>> => ({
  'supply-chain': process.env.NEXT_PUBLIC_EMBEDDED_SUPPLY_CHAIN_ORIGIN,
  'intelligence-dashboard': process.env.NEXT_PUBLIC_EMBEDDED_INTELLIGENCE_DASHBOARD_ORIGIN,
});

// Normalizes to a bare origin (the postMessage checks compare it exactly); invalid values are ignored.
const parseOrigin = (value: string | undefined): string | undefined => {
  if (!value) return undefined;

  try {
    return new URL(value).origin;
  } catch {
    return undefined;
  }
};

export class EmbeddedApps {
  static isEmbeddedAppId(id: unknown): id is EmbeddedAppId {
    return typeof id === 'string' && Object.prototype.hasOwnProperty.call(REGISTRY, id);
  }

  static get(id: string): EmbeddedAppDefinition | undefined {
    return EmbeddedApps.isEmbeddedAppId(id) ? REGISTRY[id] : undefined;
  }

  static list(): EmbeddedAppDefinition[] {
    return Object.values(REGISTRY);
  }

  // Finds the registered app an external URL points to. Both env origins match, so a prod link stored in data
  // still opens the embedded app resolved for the current deployment; the hash is the app's deep link.
  static matchUrl(value: string): { app: EmbeddedAppDefinition; hash: string } | undefined {
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return undefined;
    }

    const app = EmbeddedApps.list().find(({ origin }) => origin.dev === url.origin || origin.prod === url.origin);
    return app && { app, hash: url.hash };
  }

  static resolve(id: EmbeddedAppId): ResolvedEmbeddedApp {
    const app: EmbeddedAppDefinition = REGISTRY[id];
    const isProd = Env.isProdDeployment();
    const key: keyof ValueOf<typeof REGISTRY>['origin'] = isProd ? 'prod' : 'dev';
    const origin = (Env.isLocalhost() && parseOrigin(getOriginOverrides()[id])) || app.origin[key];

    console.debug(`[EmbeddedApps] resolve("${id}"): isProd=${isProd}, origin=${origin}, audience=${app.audience[key]}`);

    return { id, label: app.label, route: app.route, origin, audience: app.audience[key] };
  }
}
